// path: src/Components/story/StoryLevelIntro.js

import React, { useMemo } from "react";
import { getStoryLevelModifierLabels } from "../../data/storyLevels";
import { getStoryLevelRewardRow } from "../../data/storyLevelRewards";
import { BOOST_CATALOG } from "../../utils/boosts";
import BoostIcon from "../boosts/BoostIcon";

const MODIFIER_CHIP_CLASS = {
    Blitz: "gameStatsChip gameStatsChip--blitz",
    Hardcore: "gameStatsChip gameStatsChip--hardcore",
    Twisted: "gameStatsChip gameStatsChip--twisted",
    Anxious: "gameStatsChip gameStatsChip--anxious",
};

function buildRewardLines(rewardRow) {
    if (!rewardRow) {
        return [];
    }
    const lines = [];
    if (rewardRow.treats > 0) {
        lines.push(`${rewardRow.treats.toLocaleString("en-US")} Treats`);
    }
    if (rewardRow.packLabel) {
        lines.push(rewardRow.packLabel);
    }
    if (rewardRow.boostBundleLabel) {
        lines.push(rewardRow.boostBundleLabel);
    }
    (rewardRow.cosmeticLabels || []).forEach((label) => lines.push(label));
    return lines;
}

function buildUnlockLines(rewardRow) {
    if (!rewardRow) {
        return [];
    }
    return [...(rewardRow.featureUnlocks || [])];
}

export default function StoryLevelIntro({
    level,
    hardMode = false,
    onStart,
    onCancel,
}) {
    const rewardRow = useMemo(
        () => (level ? getStoryLevelRewardRow(level.id) : null),
        [level]
    );
    const modifierLabels = useMemo(
        () => (level ? getStoryLevelModifierLabels(level, { hardMode }) : []),
        [level, hardMode]
    );
    const rewardLines = useMemo(() => buildRewardLines(rewardRow), [rewardRow]);
    const unlockLines = useMemo(() => buildUnlockLines(rewardRow), [rewardRow]);

    if (!level) {
        return null;
    }

    const showBoostIntro = Number(rewardRow?.boostBundle) > 0;

    return (
        <div
            className="storyLevelIntro"
            role="dialog"
            aria-modal="true"
            aria-labelledby="storyLevelIntroTitle"
        >
            <div className="storyLevelIntro__panel">
                {typeof onCancel === "function" ? (
                    <button
                        type="button"
                        className="storyLevelIntro__close"
                        aria-label="Close level preview"
                        onClick={onCancel}
                    >
                        ×
                    </button>
                ) : null}
                <p className="storyLevelIntro__eyebrow">
                    Story Level {level.id}
                    {hardMode ? " (Hard)" : ""}
                </p>
                <h2 id="storyLevelIntroTitle" className="storyLevelIntro__title">
                    {level.title}
                    {hardMode ? " (Hard)" : ""}
                </h2>

                <ul className="storyLevelIntro__meta">
                    <li>
                        <span className="storyLevelIntro__metaLabel">
                            Modifiers
                        </span>
                        <span className="storyLevelIntro__metaValue">
                            {modifierLabels.length > 0 ? (
                                <span
                                    className="storyLevelIntro__modifierBadges"
                                    aria-label="Active modifiers"
                                >
                                    {modifierLabels.map((label) => (
                                        <span
                                            key={label}
                                            className={
                                                MODIFIER_CHIP_CLASS[label] ||
                                                "gameStatsChip"
                                            }
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </span>
                            ) : (
                                "None"
                            )}
                        </span>
                    </li>
                    <li>
                        <span className="storyLevelIntro__metaLabel">Goal</span>
                        <span className="storyLevelIntro__metaValue">
                            {level.objectiveSummary || "Clear the board once"}
                        </span>
                    </li>
                    <li>
                        <span className="storyLevelIntro__metaLabel">
                            Rewards
                        </span>
                        <span className="storyLevelIntro__metaValue">
                            {rewardLines.length > 0
                                ? rewardLines.join(" · ")
                                : "—"}
                        </span>
                    </li>
                    <li>
                        <span className="storyLevelIntro__metaLabel">
                            Unlocks
                        </span>
                        <span className="storyLevelIntro__metaValue">
                            {unlockLines.length > 0
                                ? unlockLines.join(" · ")
                                : "—"}
                        </span>
                    </li>
                </ul>

                {showBoostIntro ? (
                    <div className="storyLevelIntro__boostInfo" role="note">
                        <div className="storyLevelIntro__boostInfoTitle">
                            Boost Bundle unlocked
                        </div>
                        <p className="storyLevelIntro__boostInfoBody">
                            Boosts are spent from Inventory during Story and
                            Endless runs (not Hardcore or Blitz). Use as many as
                            you own on a board.
                        </p>
                        <ul className="storyLevelIntro__boostList">
                            {BOOST_CATALOG.map((boost) => (
                                <li key={boost.id}>
                                    <BoostIcon boost={boost} size={22} />{" "}
                                    <strong>{boost.name}</strong> —{" "}
                                    {boost.description}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                <div className="storyLevelIntro__actions">
                    <button
                        type="button"
                        className="homePrimaryButton storyLevelIntro__start"
                        onClick={onStart}
                    >
                        Start Level
                    </button>
                </div>
            </div>
        </div>
    );
}
